package com.rajen.angrejidictionary.pages

import androidx.compose.runtime.Composable
import com.rajen.angrejidictionary.components.layouts.PageLayoutData
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.OverflowWrap
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*

@InitRoute
fun initPrivacy(ctx: InitRouteContext) {
    ctx.data.add(
        PageLayoutData(
            title = "Privacy Policy",
            description = "Learn how Angreji Dictionary handles user privacy and data."
        )
    )
}

@Page("/privacy")
@Layout(".components.layouts.PageLayout")
@Composable
fun Privacy() {

    Box(
        Modifier.fillMaxWidth(),
        contentAlignment = Alignment.Center
    ) {
        Column(
            Modifier
                .fillMaxWidth()
                .maxWidth(900.px),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {

            // Header

            SpanText(
                text = "PRIVACY POLICY",
                modifier = Modifier
                    .fillMaxWidth()
                    .fontSize(clamp(1.8.cssRem, 5.vw, 2.5.cssRem))
                    .fontWeight(FontWeight.Bold)
                    .textAlign(TextAlign.Center)
                    .color(Color.rgb(0xD3, 0x35, 0x3E))
            )

            SpanText(
                text = "Effective Date: June 1, 2026",
                modifier = Modifier
                    .fillMaxWidth()
                    .margin(top = 0.5.cssRem)
                    .fontSize(1.cssRem)
                    .textAlign(TextAlign.Center)
                    .color(Color.rgb(0xB6, 0x9B, 0x8C))
            )

            Image(
                src = "/divider.png",
                alt = "Divider",
                modifier = Modifier
                    .width(220.px)
                    .margin(topBottom = 1.5.cssRem)
            )

            // Main Card

            Column(
                Modifier
                    .fillMaxWidth()
                    .backgroundColor(Color.rgb(0xFF, 0xF7, 0xF1))
                    .borderRadius(28.px)
                    .padding(2.cssRem)
                    .boxShadow(
                        offsetY = 10.px,
                        blurRadius = 30.px,
                        color = Color.rgba(0, 0, 0, 0.06f)
                    ),
                verticalArrangement = Arrangement.spacedBy(1.cssRem)
            ) {

                SpanText(
                    text = "At Angreji Dictionary, we respect your privacy and are committed to protecting information related to your use of our website and applications. This Privacy Policy explains how information may be collected, used, and safeguarded.",
                    modifier = Modifier
                        .fontSize(1.05.cssRem)
                        .lineHeight(1.9)
                        .overflowWrap(OverflowWrap.BreakWord)
                        .color(Color.rgb(0x21, 0x00, 0x00))
                        .margin(bottom = 1.cssRem)
                )

                PrivacySection(
                    "📋",
                    "Information We Collect",
                    "Angreji Dictionary may collect limited technical information such as browser type, device information, operating system details, and anonymous usage statistics to improve service performance."
                )

                PrivacySection(
                    "🔑",
                    "No Account or Personal Information",
                    "Currently, Angreji Dictionary does not require user registration or account creation. We do not collect personal information such as your name, email address, phone number, or password."
                )

                PrivacySection(
                    "🍪",
                    "Cookies and Analytics",
                    "Our website may use cookies or similar technologies to enhance user experience, analyze traffic, and understand how visitors interact with the service."
                )

                PrivacySection(
                    "📱",
                    "Application Data",
                    "Our mobile applications may collect non-personal information required for functionality, crash reporting, and performance improvements."
                )

                PrivacySection(
                    "🔍",
                    "Search Queries",
                    "Search words entered into Angreji Dictionary are used to provide dictionary results. Anonymous search statistics may be analyzed in the future to improve content quality and user experience."
                )

                PrivacySection(
                    "💡",
                    "Word Suggestions & Feedback",
                    "Angreji Dictionary allows users to suggest new words and provide feedback through our mobile applications. When you submit a word suggestion, we may collect: (1) The suggested word itself, (2) Device information including platform, device model, manufacturer, operating system version, and language preference, (3) Application version information. This information is used solely to review and potentially add suggested words to our dictionary database, improve our service quality, and enhance user experience. We do not associate word suggestions with personal identifiers unless you voluntarily provide contact information through our Contact page."
                )

                PrivacySection(
                    "🎯",
                    "How We Use Information",
                    "Collected information may be used to improve website functionality, enhance user experience, analyze service usage, and maintain platform security."
                )

                PrivacySection(
                    "🔐",
                    "Data Security",
                    "We implement reasonable security measures to protect information from unauthorized access, disclosure, or misuse. However, no internet-based service can guarantee absolute security."
                )

                PrivacySection(
                    "🤝",
                    "Third-Party Services",
                    "Angreji Dictionary may use third-party tools such as analytics providers, advertising services, or hosting platforms. These services may collect information according to their own privacy policies."
                )

                PrivacySection(
                    "🚫",
                    "No Data Selling",
                    "We do not sell, rent, trade, or share personal information with third parties for marketing or commercial purposes."
                )

                PrivacySection(
                    "🚫",
                    "Children's Privacy",
                    "Our services are intended for general audiences and are not specifically directed toward children under the age of 13."
                )

                PrivacySection(
                    "🌍",
                    "International Access",
                    "Users accessing the service from different countries are responsible for compliance with applicable local laws and regulations."
                )

                PrivacySection(
                    "🔒",
                    "Data Privacy & Security Policy",
                    "We are committed to protecting your privacy. We do not collect unnecessary personal data. All data collection is done transparently and in accordance with applicable privacy laws."
                )

                PrivacySection(
                    "📜",
                    "Governing Law",
                    "This Privacy Policy shall be governed by and interpreted in accordance with the laws of India."
                )

                PrivacySection(
                    "🔄",
                    "Changes to Privacy Policy",
                    "We may update this Privacy Policy periodically. Updated versions will be published on this page with a revised effective date."
                )

                PrivacySection(
                    "✉️",
                    "Contact Us",
                    "If you have any questions regarding this Privacy Policy, please contact us through the Contact page."
                )
            }
        }
    }
}

@Composable
private fun PrivacySection(
    icon: String,
    title: String,
    content: String
) {

    Column(
        Modifier
            .fillMaxWidth()
            .backgroundColor(Color.rgb(0xF8, 0xDD, 0xC8))
            .borderRadius(20.px)
            .padding(1.5.cssRem)
            .boxShadow(
                offsetY = 4.px,
                blurRadius = 12.px,
                color = Color.rgba(0, 0, 0, 0.05f)
            )
    ) {

        SpanText(
            text = "$icon  $title",
            modifier = Modifier
                .fontSize(1.2.cssRem)
                .fontWeight(FontWeight.Bold)
                .overflowWrap(OverflowWrap.BreakWord)
                .color(Color.rgb(0xD3, 0x35, 0x3E))
                .margin(bottom = 0.75.cssRem)
        )

        SpanText(
            text = content,
            modifier = Modifier
                .fontSize(1.cssRem)
                .lineHeight(1.8)
                .overflowWrap(OverflowWrap.BreakWord)
                .color(Color.rgb(0x21, 0x00, 0x00))
        )
    }
}